import { Lock, Bell, Shield, Crown, CheckCircle } from "lucide-react"
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import StorefrontStoreIdCard from "../../components/Storefront/StorefrontStoreIdCard"
import SubscriptionModal from "../../components/subscrptions/SubscriptionModal"
import { GESTOCK_PLANS } from "../../config/subscriptions"
import { runSubscriptionUpgrade } from "../../utils/subscriptionUpgrade"
import { getCurrentPlan } from "../../utils/permissions"

export default function Settings() {
  const { userdata } = useSelector((state: any) => state.UserReducer)
  const dispatch = useDispatch()
  const currentPlan = getCurrentPlan(userdata)
  const availablePlans = userdata?.availablePlans
  const user = userdata?.user
  const currentBillingCycle = userdata?.subscription?.billingCycle || userdata?.user?.subscriptionBillingCycle || "monthly"

  const [openSubscription, setOpenSubscription] = useState(false)
  const currentPlanLabel =
    GESTOCK_PLANS[currentPlan as keyof typeof GESTOCK_PLANS]?.name || currentPlan

  return (
    <div className="max-w-4xl mx-auto space-y-6 px-4 sm:px-0">

      {/* HEADER */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Paramètres
        </h1>
        <p className="text-gray-500">
          Sécurité, préférences et abonnement Gestock
        </p>
      </div>

      {/* STORE ID */}
      <StorefrontStoreIdCard store={userdata?.store} />

      {/* SETTINGS SECTIONS */}
      <div className="bg-white rounded-2xl border shadow-sm divide-y">

        {/* PASSWORD */}
        <SettingItem
          icon={<Lock />}
          title="Mot de passe"
          description="Modifier votre mot de passe"
          action="Changer"
          onClick={() => {}}
        />

        {/* NOTIFICATIONS */}
        <SettingItem
          icon={<Bell />}
          title="Notifications"
          description="Activer ou désactiver les alertes"
          action="Configurer"
          onClick={() => {}}
        />

        {/* SECURITY */}
        <SettingItem
          icon={<Shield />}
          title="Sécurité"
          description="Gestion des accès et des sessions"
          action="Voir"
          onClick={() => {}}
        />

        {/* ================= SUBSCRIPTION ================= */}
        <div className="p-6 space-y-4 bg-gradient-to-r from-blue-50 to-indigo-50">

          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-xl bg-yellow-100 text-yellow-600 flex items-center justify-center">
              <Crown />
            </div>

            <div className="flex-1">
              <p className="font-semibold text-gray-800">
                Abonnement Gestock
              </p>
              <p className="text-sm text-gray-600">
                Débloquez Mobile Money, e-commerce et fonctionnalités avancées
              </p>
            </div>
          </div>

          {/* PLAN INFO */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

            <div className="space-y-1">
              <p className="text-sm text-gray-500">
                Plan actuel
              </p>

              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-600" size={18} />
                <span className="font-semibold text-gray-800">
                  {currentPlanLabel}
                </span>
              </div>
            </div>

            <button
              onClick={() => setOpenSubscription(true)}
              className="
                px-5 py-2.5 rounded-xl
                bg-blue-600 text-white font-medium
                hover:bg-blue-700 transition
                w-full sm:w-auto
              "
            >
              Mettre à niveau
            </button>
          </div>
        </div>
      </div>

      {/* SUBSCRIPTION MODAL */}
      <SubscriptionModal
        isOpen={openSubscription}
        currentPlan={currentPlan}
        currentBillingCycle={currentBillingCycle}
        plans={availablePlans}
        onClose={() => setOpenSubscription(false)}
        onUpgrade={(planKey, billingCycle) =>
          runSubscriptionUpgrade({
            planKey,
            billingCycle,
            currentPlan,
            currentBillingCycle,
            user,
            dispatch,
            onClose: () => setOpenSubscription(false),
          })
        }
      />
    </div>
  )
}

/* ---------- COMPONENT ---------- */

function SettingItem({
  icon,
  title,
  description,
  action,
  onClick,
}: {
  icon: React.ReactNode
  title: string
  description: string
  action: string
  onClick: () => void
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 gap-4">
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center">
          {icon}
        </div>

        <div>
          <p className="font-semibold text-gray-800">{title}</p>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>

      <button
        onClick={onClick}
        className="
          px-4 py-2 rounded-xl
          border text-sm font-medium
          hover:bg-gray-50 transition
          w-full sm:w-auto
        "
      >
        {action}
      </button>
    </div>
  )
}
